import { __awaiter, __generator, __read, __values } from "tslib";
import { TransactionMode, QueryAggregateType, } from '@tencent/memoire-core';
import QueryDelegate from '../query/query-delegate';
import RawQueryBuilder from '../query/raw-query-builder';
import { convertModelToPersistentData } from '../utils/model-data-converter';
var StructuredCollectionSlice = /** @class */ (function () {
    function StructuredCollectionSlice(name, options) {
        this.name = name;
        this.options = options;
        this.contextGenerator = options.contextGenerator;
        this.queryBuilder = new RawQueryBuilder();
        this.patch();
    }
    StructuredCollectionSlice.toggleOptimization = function (enabled) {
        return QueryDelegate.toggleOptimization(enabled);
    };
    StructuredCollectionSlice.prototype.skip = function (offset) {
        this.queryBuilder.setOffset(offset);
        return this;
    };
    StructuredCollectionSlice.prototype.take = function (limit) {
        this.queryBuilder.setLimit(limit);
        return this;
    };
    StructuredCollectionSlice.prototype.slice = function (start, end) {
        this.queryBuilder.setRange(start, end);
        return this;
    };
    StructuredCollectionSlice.prototype.orderBy = function (path, order) {
        this.queryBuilder.setOrderBy(path, order);
        return this;
    };
    StructuredCollectionSlice.prototype.where = function (descriptor) {
        this.queryBuilder.addWhereClause(descriptor);
        return this;
    };
    StructuredCollectionSlice.prototype.or = function (descriptor) {
        this.queryBuilder.addWhereClause(descriptor);
        return this;
    };
    StructuredCollectionSlice.prototype.conditions = function () {
        var context = this.contextGenerator.spawnContext();
        var queryDelegate = new QueryDelegate(this.name, { context: context });
        return queryDelegate.conditions(this.queryBuilder.build());
    };
    StructuredCollectionSlice.prototype.clone = function () {
        var cloned = new StructuredCollectionSlice(this.name, this.options);
        cloned.queryBuilder = this.queryBuilder.clone();
        return cloned;
    };
    StructuredCollectionSlice.prototype.explain = function (aggregate) {
        return __awaiter(this, void 0, void 0, function () {
            var context, queryDelegate;
            return __generator(this, function (_a) {
                context = this.contextGenerator.spawnContext();
                queryDelegate = new QueryDelegate(this.name, { context: context });
                return [2 /*return*/, queryDelegate.explain(this.queryBuilder.build(), aggregate)];
            });
        });
    };
    StructuredCollectionSlice.prototype.count = function () {
        return __awaiter(this, void 0, void 0, function () {
            var context;
            var _this = this;
            return __generator(this, function (_a) {
                context = this.contextGenerator.spawnContext();
                return [2 /*return*/, context.perform(function () { return __awaiter(_this, void 0, void 0, function () {
                        var queryDelegate;
                        return __generator(this, function (_a) {
                            queryDelegate = new QueryDelegate(this.name, { context: context });
                            return [2 /*return*/, queryDelegate.exec(this.queryBuilder.build(), QueryAggregateType.COUNT)];
                        });
                    }); })];
            });
        });
    };
    StructuredCollectionSlice.prototype.entries = function () {
        return __awaiter(this, void 0, void 0, function () {
            var context;
            var _this = this;
            return __generator(this, function (_a) {
                context = this.contextGenerator.spawnContext();
                return [2 /*return*/, context.perform(function () { return __awaiter(_this, void 0, void 0, function () {
                        var queryDelegate;
                        return __generator(this, function (_a) {
                            queryDelegate = new QueryDelegate(this.name, { context: context });
                            return [2 /*return*/, queryDelegate.exec(this.queryBuilder.build(), QueryAggregateType.ENTRIES)];
                        });
                    }); })];
            });
        });
    };
    StructuredCollectionSlice.prototype.keys = function () {
        return __awaiter(this, void 0, void 0, function () {
            var context;
            var _this = this;
            return __generator(this, function (_a) {
                context = this.contextGenerator.spawnContext();
                return [2 /*return*/, context.perform(function () { return __awaiter(_this, void 0, void 0, function () {
                        var queryDelegate;
                        return __generator(this, function (_a) {
                            queryDelegate = new QueryDelegate(this.name, { context: context });
                            return [2 /*return*/, queryDelegate.exec(this.queryBuilder.build(), QueryAggregateType.KEYS)];
                        });
                    }); })];
            });
        });
    };
    StructuredCollectionSlice.prototype.values = function () {
        return __awaiter(this, void 0, void 0, function () {
            var context;
            var _this = this;
            return __generator(this, function (_a) {
                context = this.contextGenerator.spawnContext();
                return [2 /*return*/, context.perform(function () { return __awaiter(_this, void 0, void 0, function () {
                        var queryDelegate;
                        return __generator(this, function (_a) {
                            queryDelegate = new QueryDelegate(this.name, { context: context });
                            return [2 /*return*/, queryDelegate.exec(this.queryBuilder.build(), QueryAggregateType.VALUES)];
                        });
                    }); })];
            });
        });
    };
    StructuredCollectionSlice.prototype.first = function () {
        return __awaiter(this, void 0, void 0, function () {
            var context;
            var _this = this;
            return __generator(this, function (_a) {
                context = this.contextGenerator.spawnContext();
                return [2 /*return*/, context.perform(function () { return __awaiter(_this, void 0, void 0, function () {
                        var queryDelegate, values;
                        var _a;
                        return __generator(this, function (_b) {
                            switch (_b.label) {
                                case 0:
                                    queryDelegate = new QueryDelegate(this.name, { context: context });
                                    return [4 /*yield*/, queryDelegate.exec(this.queryBuilder.build(), QueryAggregateType.VALUES)];
                                case 1:
                                    values = _b.sent();
                                    return [2 /*return*/, (_a = values[0]) !== null && _a !== void 0 ? _a : null];
                            }
                        });
                    }); })];
            });
        });
    };
    StructuredCollectionSlice.prototype.last = function () {
        return __awaiter(this, void 0, void 0, function () {
            var context;
            var _this = this;
            return __generator(this, function (_a) {
                context = this.contextGenerator.spawnContext();
                return [2 /*return*/, context.perform(function () { return __awaiter(_this, void 0, void 0, function () {
                        var queryDelegate, values;
                        var _a;
                        return __generator(this, function (_b) {
                            switch (_b.label) {
                                case 0:
                                    queryDelegate = new QueryDelegate(this.name, { context: context });
                                    return [4 /*yield*/, queryDelegate.exec(this.queryBuilder.build(), QueryAggregateType.VALUES)];
                                case 1:
                                    values = _b.sent();
                                    return [2 /*return*/, (_a = values[values.length - 1]) !== null && _a !== void 0 ? _a : null];
                            }
                        });
                    }); })];
            });
        });
    };
    StructuredCollectionSlice.prototype.update = function (updater) {
        return __awaiter(this, void 0, void 0, function () {
            var entries, context;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.entries()];
                    case 1:
                        entries = _a.sent();
                        context = this.contextGenerator.spawnContext();
                        return [2 /*return*/, context.perform(function () { return __awaiter(_this, void 0, void 0, function () {
                                var results, transaction, store, entries_1, entries_1_1, _a, key, value, cursor, data, e_1_1;
                                var e_1, _b;
                                return __generator(this, function (_c) {
                                    switch (_c.label) {
                                        case 0:
                                            results = [];
                                            return [4 /*yield*/, context.getTransaction([this.name], TransactionMode.READ_WRITE)];
                                        case 1:
                                            transaction = _c.sent();
                                            store = transaction.table(this.name);
                                            _c.label = 2;
                                        case 2:
                                            _c.trys.push([2, 8, 9, 10]);
                                            entries_1 = __values(entries), entries_1_1 = entries_1.next();
                                            _c.label = 3;
                                        case 3:
                                            if (!!entries_1_1.done) return [3 /*break*/, 7];
                                            _a = __read(entries_1_1.value, 2), key = _a[0], value = _a[1];
                                            return [4 /*yield*/, store.openCursor(key)];
                                        case 4:
                                            cursor = _c.sent();
                                            if (!cursor) return [3 /*break*/, 6];
                                            data = updater(value);
                                            return [4 /*yield*/, cursor.update(convertModelToPersistentData(data))];
                                        case 5:
                                            _c.sent();
                                            results.push(key);
                                            _c.label = 6;
                                        case 6:
                                            entries_1_1 = entries_1.next();
                                            return [3 /*break*/, 3];
                                        case 7: return [3 /*break*/, 10];
                                        case 8:
                                            e_1_1 = _c.sent();
                                            e_1 = { error: e_1_1 };
                                            return [3 /*break*/, 10];
                                        case 9:
                                            try {
                                                if (entries_1_1 && !entries_1_1.done && (_b = entries_1.return)) _b.call(entries_1);
                                            }
                                            finally { if (e_1) throw e_1.error; }
                                            return [7 /*endfinally*/];
                                        case 10: return [2 /*return*/, results];
                                    }
                                });
                            }); })];
                }
            });
        });
    };
    StructuredCollectionSlice.prototype.clear = function () {
        return __awaiter(this, void 0, void 0, function () {
            var keys, context;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.keys()];
                    case 1:
                        keys = _a.sent();
                        context = this.contextGenerator.spawnContext();
                        return [2 /*return*/, context.perform(function () { return __awaiter(_this, void 0, void 0, function () {
                                var results, transaction, store, keys_1, keys_1_1, key, cursor, e_2_1;
                                var e_2, _a;
                                return __generator(this, function (_b) {
                                    switch (_b.label) {
                                        case 0:
                                            results = [];
                                            return [4 /*yield*/, context.getTransaction([this.name], TransactionMode.READ_WRITE)];
                                        case 1:
                                            transaction = _b.sent();
                                            store = transaction.table(this.name);
                                            if (!store.supports('bulkDelete')) return [3 /*break*/, 3];
                                            return [4 /*yield*/, store.bulkDelete(keys)];
                                        case 2:
                                            _b.sent();
                                            return [2 /*return*/, keys];
                                        case 3:
                                            _b.trys.push([3, 9, 10, 11]);
                                            keys_1 = __values(keys), keys_1_1 = keys_1.next();
                                            _b.label = 4;
                                        case 4:
                                            if (!!keys_1_1.done) return [3 /*break*/, 8];
                                            key = keys_1_1.value;
                                            return [4 /*yield*/, store.openCursor(key)];
                                        case 5:
                                            cursor = _b.sent();
                                            if (!cursor) return [3 /*break*/, 7];
                                            return [4 /*yield*/, cursor.delete()];
                                        case 6:
                                            _b.sent();
                                            results.push(key);
                                            _b.label = 7;
                                        case 7:
                                            keys_1_1 = keys_1.next();
                                            return [3 /*break*/, 4];
                                        case 8: return [3 /*break*/, 11];
                                        case 9:
                                            e_2_1 = _b.sent();
                                            e_2 = { error: e_2_1 };
                                            return [3 /*break*/, 11];
                                        case 10:
                                            try {
                                                if (keys_1_1 && !keys_1_1.done && (_a = keys_1.return)) _a.call(keys_1);
                                            }
                                            finally { if (e_2) throw e_2.error; }
                                            return [7 /*endfinally*/];
                                        case 11: return [2 /*return*/, results];
                                    }
                                });
                            }); })];
                }
            });
        });
    };
    StructuredCollectionSlice.prototype.patch = function () {
        var aspects = this.options.aspects;
        aspects.update.patch(this, 'update');
        aspects.clear.patch(this, 'clear');
        aspects.count.patch(this, 'count');
        aspects.entries.patch(this, 'entries');
        aspects.keys.patch(this, 'keys');
        aspects.values.patch(this, 'values');
        aspects.first.patch(this, 'first');
        aspects.last.patch(this, 'last');
        aspects.skip.patch(this, 'skip');
        aspects.take.patch(this, 'take');
        aspects.slice.patch(this, 'slice');
        aspects.orderBy.patch(this, 'orderBy');
        aspects.where.patch(this, 'where');
        aspects.or.patch(this, 'or');
    };
    return StructuredCollectionSlice;
}());
export default StructuredCollectionSlice;
//# sourceMappingURL=structured-collection-slice.js.map